import { invokeSupabaseEdgeFunction } from "@/lib/invokeSupabaseEdgeFunction";
import { supabase } from "@/lib/supabase";

type InviteOrgAdminResponse = { ok?: boolean; error?: string; message?: string; email?: string };

type AcceptInviteResponse = { ok?: boolean; email?: string; error?: string; message?: string };

export async function inviteOrgAdminForOrganization(
  organizationId: string,
  email: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) {
    return { ok: false, message: "Email is required." };
  }

  const vitePublic =
    typeof import.meta.env.VITE_PUBLIC_APP_URL === "string" ? import.meta.env.VITE_PUBLIC_APP_URL.trim() : "";
  const origin =
    vitePublic || (typeof window !== "undefined" ? window.location.origin : "").trim() || undefined;

  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session?.access_token) {
    return { ok: false, message: "You must be signed in to send an invite." };
  }

  const { data, error } = await invokeSupabaseEdgeFunction<InviteOrgAdminResponse>("invite-org-admin", {
    body: {
      organization_id: organizationId,
      email: trimmed,
      ...(origin ? { public_app_url: origin } : {}),
    },
  });

  if (error) {
    return { ok: false, message: error.message };
  }
  if (!data || data.ok !== true) {
    const msg =
      typeof data?.message === "string"
        ? data.message
        : typeof data?.error === "string"
          ? data.error
          : "Invite could not be sent.";
    return { ok: false, message: msg };
  }
  return { ok: true };
}

/** Org admin or employee or portal contact: same Edge handler (`accept-org-admin-invite`). */
export async function acceptInvite(
  token: string,
  password: string,
  inviteSource?: "employee" | "org_admin" | "portal_contact" | "staff",
): Promise<{ ok: true; email: string } | { ok: false; message: string }> {
  const body: Record<string, unknown> = {
    token: token.trim(),
    password,
    /** Explicit `auto` helps the Edge handler when the query param is stripped (mail scanners, copied links). */
    invite_source: inviteSource ?? "auto",
  };

  const { data, error } = await invokeSupabaseEdgeFunction<AcceptInviteResponse>("accept-org-admin-invite", {
    body,
  });

  if (error) {
    return { ok: false, message: error.message };
  }
  if (!data || data.ok !== true) {
    const msg =
      typeof data?.message === "string"
        ? data.message
        : typeof data?.error === "string"
          ? data.error
          : "Could not complete signup.";
    return { ok: false, message: msg };
  }
  const email = typeof data.email === "string" ? data.email : "";
  if (!email) {
    return { ok: false, message: "Signup succeeded but email was not returned." };
  }
  return { ok: true, email };
}

export async function acceptOrgAdminInvite(
  token: string,
  password: string,
): Promise<{ ok: true; email: string } | { ok: false; message: string }> {
  return acceptInvite(token, password);
}
